import React, { useEffect, useState, useRef } from "react";
import "./jw-audio.m.css";
import { currentUser } from "@quintype/bridgekeeper-js";
import { getAStory } from "../../../../../../../api/utils";
import { getStoryBySection } from "../../../../../../../api/utils";

const JWaudio = ({ uniqueId, sendDataToParent, videoObject, storyAttributes, storyObj={} }) => {
  const playerId = uniqueId;
  const [videoCompleted, setVideoCompleted] = useState(false);
  const { singleVideoFile, libraryId } = videoObject;
  const [lastPosition, setLastPosition] = useState(0);
  const [userId, setUserId] = useState(null);
  const playerRef = useRef(null); // Ref to prevent re-renders
  const apiPostUrl = `/user-history/addOrUpdate`;

  useEffect(() => {
    const fetchUserId = async () => {
      try {
      const currentUserResp = await currentUser();
      const userId = currentUserResp?.user?.id || null;
      setUserId(userId);
      } catch (error) {
        console.error("Error fetching user id:", error);
      }
    };
    fetchUserId();
  }, []);

  const handlePostRequest = async (storyID, position) => {
    const currentUserResp = await currentUser();
    const userId = currentUserResp?.user?.id || null;
    if (!userId) return;

    // prepare the metadata field
    let objMetadata={};
    objMetadata["slug"] = storyObj?.slug || "";
    objMetadata["headline"] = storyObj?.headline || "";
    objMetadata["hero-image-s3-key"] = storyObj?.["hero-image-s3-key"] || "";
    objMetadata["hero-image-metadata"] = storyObj?.["hero-image-metadata"] || {};
    objMetadata["story-type"] = storyObj?.metadata?.["story-attributes"]?.storytype?.[0] || "";

    const data = { user_id: userId, media_id: storyID, last_position_media_time: position, metadata: objMetadata };
    try {
      const response = await fetch(apiPostUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (response.ok) {
        console.log("Successfully posted data");
      } else {
        console.error("Failed to post data");
      }
    } catch (error) {
      console.error("some error:", error);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!userId) return;
      const apiUrl = `/user-history/getByMediaId`;
      try {
        const data = { user_id: userId, media_id: uniqueId };
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data),
        });
        if (response.ok) {
          const data = await response.json();
          if (data?.data?.last_position_media_time) {
            const lastPos = `${data?.data?.last_position_media_time}`
            setLastPosition(lastPos);
          }
        } else {
          console.error("Failed to fetch data");
        }
      } catch (err) {
        console.log("ERROR WHILE FETCHING USER HISTORY audio video player",err)
        console.error("Error fetching data:", err);
      }
    };

    fetchData();
  }, [uniqueId,userId]);

  const setupJWPlayerEvents = (player, isMounted) => {
    player.on("ready", () => {
      const playerContainer = document.getElementById(playerId);

      // Create control container
      const controlContainer = document.createElement("div");
      controlContainer.className = "jw-custom-controls";

      // Create Rewind Button
      const rewindBtn = document.createElement("div");
      rewindBtn.className = "jw-skip-backward";
      rewindBtn.innerHTML = '<i class="ri-replay-15-line"></i>';
      rewindBtn.onclick = () => {
        player.seek(Math.max(0, player.getPosition() - 15));
      };

      // Create Forward Button
      const forwardBtn = document.createElement("div");
      forwardBtn.className = "jw-skip-forward";
      forwardBtn.innerHTML = '<i class="ri-forward-30-line"></i>';
      forwardBtn.onclick = () => {
        player.seek(Math.min(player.getDuration(), player.getPosition() + 30));
      };

      // Append buttons inside the control container
      controlContainer.appendChild(rewindBtn);
      controlContainer.appendChild(forwardBtn);
      playerContainer.appendChild(controlContainer);
    });

    player.on("pause", () => isMounted && handlePostRequest(uniqueId, player.getPosition()));
    player.on("seek", (event) => isMounted && handlePostRequest(uniqueId, event.offset));

    player.on("complete", () => {
      // set the player "seek" to "0", so that during "history.back" event, correct time is recorded in db
      player.seek("0.0");

      const getNextStory = (currentStory, storyList) => {
        if (!currentStory || !storyList || storyList.length === 0) {
          return null;
        }

        // Sort the stories by published date in ascending order
        const sortedStories = [...storyList].sort((a, b) => new Date(a["published-at"]) - new Date(b["published-at"]));

        // Find the next story whose published date is just greater than the current story
        return sortedStories.find(story => new Date(story["published-at"]) > new Date(currentStory["published-at"])) || null;
      };

      if (isMounted) {
        setVideoCompleted(true);
        getAStory(uniqueId)
          .then((story) => {
            if (story?.sections?.length > 0) {
              const firstSectionId = story?.sections?.[0]?.id ?? null;

              if (firstSectionId) {
                getStoryBySection(firstSectionId)
                  .then((stories) => {
                    const nextStory = getNextStory(story, stories);
                    const nextStoryID = nextStory?.id ?? null;
                    if (nextStoryID) {
                      // add the next episode to the user history
                      handlePostRequest(nextStoryID, "0.1");
                    }
                  })
                  .catch((error) => {
                    console.error("Error fetching stories:", error);
                  });
              }
            }
          })
          .catch((error) => {
            console.error("Error fetching the story:", error);
          });

          // update the current video's timestamp to 0, as it's fully watched now
      }
    });
  };

  const initializeJWPlayer = (isMounted) => {
    if (playerRef.current) return; // Prevent reinitialization

    const player = window.jwplayer(playerId).setup({
      playlist: singleVideoFile,
      skin: {
        name: "seven",
        controlbar: { background: "#FFFFFF", icons: "rgba(64, 64, 64, 0.8)" },
        timeslider: { progress: "rgba(116, 11, 11, 0.8)", rail: "#FFFFFF" },
      },
      allowFullscreen: true,
      autostart: true
    });

    if (lastPosition && isMounted) {
      player.once("play", () => player.seek(lastPosition));
    }

    setupJWPlayerEvents(player, isMounted);
    playerRef.current = player;
  };

  useEffect(() => {
    let isMounted = true;

    const loadJWPlayerScript = () => {
      if (window.jwplayer) {
        initializeJWPlayer(isMounted);
        return;
      }

      const script = document.createElement("script");
      script.src = `https://cdn.jwplayer.com/libraries/${libraryId}.js`;
      script.async = true;
      script.onload = () => isMounted && initializeJWPlayer(isMounted);
      document.body.appendChild(script);
    };

    if (document.getElementById(playerId)) {
      loadJWPlayerScript();
    } else {
      console.error("Player container is not yet mounted in the DOM");
    }

    const handleBeforeUnload = () => {
      if (window.jwplayer && isMounted) {
        handlePostRequest(uniqueId, window.jwplayer(playerId).getPosition());
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      isMounted = false;
      if (playerRef.current) {
        playerRef.current.remove();
        playerRef.current = null;
      }
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [singleVideoFile, libraryId, playerId, lastPosition]);

  useEffect(() => {
    if (videoCompleted) {
      sendDataToParent(true);
    }
  }, [videoCompleted, sendDataToParent]);

  useEffect(() => {
    const handleWrapperPostRequest  = () => {
      if (window.jwplayer && playerId) {
        const currentTime = window.jwplayer(playerId).getPosition();
        handlePostRequest(uniqueId, !currentTime ? "0.0" : currentTime);
      }
    };

    const handleNavigationClick = (event) => {
      const target = event.target.closest("a, .ri-arrow-left-line");

      if (target) {
        handleWrapperPostRequest();
      }
    };

    const handleBackButton = () => {
      handleWrapperPostRequest();
    };

    document.addEventListener("click", handleNavigationClick);
    window.addEventListener("popstate", handleBackButton);

    return () => {
      document.removeEventListener("click", handleNavigationClick);
      window.removeEventListener("popstate", handleBackButton);
    };
  }, []);

  return <div className="audioSkin">
          <div id={playerId}></div>
        </div>;
};

export default JWaudio;
